/**
 * SessionsTable component - Production Monitor style
 * Displays all sessions for the selected date in the same format as the Production Monitor
 * Shows: Session, Product, Operator, Station, Elapsed, Progress, Output, Pace, Status, Remaining
 */
import React from 'react';
import { useLanguage } from '../../../../i18n/LanguageContext';
import { SessionSummary } from '../../../../services/productionService';
import { Clock, TrendingUp, User } from 'lucide-react';
import { getStatusBadgeClass } from '../../../../utils/statusColors';

interface SessionsTableProps {
  sessions: SessionSummary[];
  onSessionClick?: (sessionId: string) => void;
}

// Format elapsed time from seconds as "Xh Xm"
const formatElapsedSeconds = (totalSeconds: number): string => {
  const h = Math.floor(totalSeconds / 3600);
  const m = Math.floor((totalSeconds % 3600) / 60);
  return `${h}h ${m}m`;
};

// Format timestamp to readable date and time (e.g., "Jan 30, 9:11 AM")
const formatDateTime = (timestamp: string | null): string => {
  if (!timestamp) return '-';
  const date = new Date(timestamp);
  return date.toLocaleString(undefined, {
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
  });
};

const SessionsTable: React.FC<SessionsTableProps> = ({ sessions, onSessionClick }) => {
  const { t } = useLanguage();

  // Get translated status label
  const getStatusLabel = (status: string): string => {
    switch (status) {
      case 'completed': return t('common.done');
      case 'active': return t('status.active');
      case 'paused': return t('status.paused');
      case 'ready': return t('status.ready');
      case 'received': return t('status.planned');
      case 'cancelled': return t('common.cancelled');
      default: return status;
    }
  };

  if (sessions.length === 0) {
    return (
      <div className="text-center py-8 text-gray-500 dark:text-gray-400">
        {t('endOfDaySummary.noSessions')}
      </div>
    );
  }

  return (
    <div className="overflow-x-auto">
      <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
        <thead className="bg-gray-50 dark:bg-gray-900/50">
          <tr>
            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
              {t('productionMonitor.session')}
            </th>
            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
              {t('productionMonitor.product')}
            </th>
            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
              {t('productionMonitor.operator')}
            </th>
            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
              {t('productionMonitor.station')}
            </th>
            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
              {t('common.startedAt')}
            </th>
            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
              {t('common.completedAt')}
            </th>
            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
              {t('productionMonitor.elapsed')}
            </th>
            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
              {t('productionMonitor.progress')}
            </th>
            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
              {t('productionMonitor.output')}
            </th>
            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
              {t('productionMonitor.pace')}
            </th>
            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
              {t('productionMonitor.status')}
            </th>
            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
              {t('productionMonitor.remaining')}
            </th>
          </tr>
        </thead>
        <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
          {sessions.map((session) => {
            // Use backend-calculated elapsed seconds (wall clock minus pauses)
            const elapsedSeconds = session.elapsed_seconds || 0;
            const elapsedHours = elapsedSeconds / 3600;
            const boxesPacked = session.boxes_packed || 0;
            const expectedBoxes = session.expected_boxes || boxesPacked || 1; // Use packed as expected if not available
            const progressPercent = expectedBoxes > 0 ? Math.min((boxesPacked / expectedBoxes) * 100, 100) : (boxesPacked > 0 ? 100 : 0);
            const totalWeight = session.total_weight || 0;
            const targetWeight = session.target_weight || totalWeight || 0;

            // Calculate pace (cases/hr and lbs/hr)
            const casesPerHour = elapsedHours >= 0.1 ? boxesPacked / elapsedHours : 0;
            const lbsPerHour = elapsedHours >= 0.1 ? totalWeight / elapsedHours : 0;

            const remainingCases = session.remaining_cases ?? 0;
            const remainingLbs = session.remaining_weight ?? 0;

            const statusColor = getStatusBadgeClass(session.status);
            const statusLabel = getStatusLabel(session.status);

            return (
              <tr
                key={session.session_number}
                className={`hover:bg-gray-50 dark:hover:bg-gray-700 ${onSessionClick ? 'cursor-pointer' : ''} transition-colors`}
                onClick={() => onSessionClick?.(session.session_number)}
              >
                {/* Session Number */}
                <td className="px-4 py-3 whitespace-nowrap">
                  <span className="font-mono text-sm text-green-600 dark:text-green-400 font-semibold">
                    {session.session_number}
                  </span>
                </td>

                {/* Product */}
                <td className="px-4 py-3">
                  <div className="text-xs font-mono text-gray-500 dark:text-gray-400">
                    {session.product_sku}
                  </div>
                  <div className="text-sm font-medium text-gray-900 dark:text-white">
                    {session.product_name}
                  </div>
                  {session.product_chinese_name && (
                    <div className="text-xs text-gray-500 dark:text-gray-400">
                      {session.product_chinese_name}
                    </div>
                  )}
                </td>

                {/* Operator */}
                <td className="px-4 py-3 whitespace-nowrap text-sm">
                  {session.operator_names && session.operator_names.length > 0 ? (
                    <div className="flex items-center gap-1.5 text-gray-600 dark:text-gray-300">
                      <User className="w-3.5 h-3.5 text-green-500" />
                      {session.operator_names.length > 1
                        ? `${session.operator_names.length} operators`
                        : session.operator_names[0]
                      }
                    </div>
                  ) : (
                    <span className="text-gray-400">-</span>
                  )}
                </td>

                {/* Station */}
                <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-600 dark:text-gray-300">
                  {session.station_names && session.station_names.length > 0
                    ? session.station_names.join(', ')
                    : '-'
                  }
                </td>

                {/* Started At */}
                <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-600 dark:text-gray-300">
                  {formatDateTime(session.start_time)}
                </td>

                {/* Completed At */}
                <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-600 dark:text-gray-300">
                  {formatDateTime(session.end_time)}
                </td>

                {/* Elapsed */}
                <td className="px-4 py-3 whitespace-nowrap">
                  <div className="flex items-center gap-1 text-sm text-green-600 dark:text-green-400">
                    <Clock className="w-3 h-3" />
                    {formatElapsedSeconds(elapsedSeconds)}
                  </div>
                </td>

                {/* Progress */}
                <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-600 dark:text-gray-300">
                  {progressPercent.toFixed(2)}%
                </td>

                {/* Output */}
                <td className="px-4 py-3 whitespace-nowrap text-sm">
                  <div className="text-gray-600 dark:text-gray-300">
                    <span className="font-medium">{boxesPacked}</span>
                    <span className="text-gray-400"> / {expectedBoxes} cases</span>
                  </div>
                  <div className="text-xs text-gray-500 dark:text-gray-400">
                    {totalWeight.toFixed(2)} / {targetWeight.toFixed(2)} lbs
                  </div>
                </td>

                {/* Pace */}
                <td className="px-4 py-3 whitespace-nowrap">
                  <div className="flex items-center gap-1 text-sm text-gray-600 dark:text-gray-300">
                    <TrendingUp className="w-3 h-3" />
                    {casesPerHour.toFixed(2)} cases/hr
                  </div>
                  <div className="text-xs text-gray-500 dark:text-gray-400">
                    {lbsPerHour.toFixed(0)} lbs/hr
                  </div>
                </td>

                {/* Status */}
                <td className="px-4 py-3 whitespace-nowrap">
                  <span className={`inline-flex px-2 py-1 text-xs font-medium rounded-full ${statusColor}`}>
                    {statusLabel}
                  </span>
                </td>

                {/* Remaining */}
                <td className="px-4 py-3 whitespace-nowrap text-sm">
                  <div className="text-gray-600 dark:text-gray-300">
                    {remainingLbs.toFixed(2)} lbs
                  </div>
                  <div className="text-xs text-gray-500 dark:text-gray-400">
                    {remainingCases} cases
                  </div>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
};

export default SessionsTable;
